import { sanitizeTemplate } from "./labels.js";

const decodeHtml = (value = "") => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const stripHtmlToLines = (html = "") =>
  sanitizeTemplate(html)
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(div|p|li|tr|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .split("\n")
    .map((line) => decodeHtml(line).trim())
    .filter(Boolean);

const escapeZplField = (value = "") =>
  String(value).replace(/\^/g, " ").replace(/~/g, " ");

const BLOCK_TAGS = [
  "BLOCKQUOTE",
  "DIV",
  "P",
  "LI",
  "TR",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
];
const BORDER_SIDES = ["top", "right", "bottom", "left"];

const LABEL_ORIENTATIONS = {
  portrait: "portrait",
  landscape: "landscape",
};

// Zebra ZT231 commonly runs at 203 dpi in this setup, so width is converted
// from mm to printer dots before composing the ZPL.
const ZT231_DOTS_PER_MM = 203 / 25.4;

const normalizeOrientation = (orientation = LABEL_ORIENTATIONS.portrait) =>
  orientation === LABEL_ORIENTATIONS.landscape
    ? LABEL_ORIENTATIONS.landscape
    : LABEL_ORIENTATIONS.portrait;

const parsePx = (value, fallback = null) => {
  const match = String(value || "").match(/([\d.]+)\s*px/i);
  const parsedValue = match ? Number(match[1]) : Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const parseColumnCount = (value, fallback = 1) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue)
    ? Math.min(Math.max(Math.round(parsedValue), 1), 4)
    : fallback;
};

const parseBorderWidth = (style, side = "") => {
  const suffix = side ? `${side[0].toUpperCase()}${side.slice(1)}` : "";
  const borderWidth = style[`border${suffix}Width`] || style.borderWidth;

  if (borderWidth) return parsePx(borderWidth, 0);

  const border = style[`border${suffix}`] || style.border || "";
  const match = String(border).match(/([\d.]+)\s*px/i);

  return match ? Number(match[1]) : 0;
};

const parseTextDecoration = (value = "") =>
  String(value || "")
    .split(/\s+/)
    .map((item) => item.trim())
    .filter(Boolean);

const parseBorderStyle = (style, side = "", inheritedStyle = {}) => {
  const suffix = side ? `${side[0].toUpperCase()}${side.slice(1)}` : "";

  return (
    style[`border${suffix}Style`] ||
    style.borderStyle ||
    inheritedStyle?.border?.[side]?.style ||
    inheritedStyle.borderStyle ||
    "solid"
  );
};

const getBorderSide = (style, side, inheritedStyle = {}) => {
  const borderStyle = parseBorderStyle(style, side, inheritedStyle);
  const borderWidth = Math.max(
    parseBorderWidth(style, side),
    parseBorderWidth(style),
    inheritedStyle?.border?.[side]?.width || 0
  );

  return {
    width: borderStyle === "none" ? 0 : borderWidth,
    style: borderStyle,
  };
};

const getInlineStyle = (element, inheritedStyle = {}) => {
  const style = element?.style || {};
  const fontSize = parsePx(style.fontSize, inheritedStyle.fontSize || 30);
  const tagName = element?.tagName || "";
  const textDecorations = [
    ...(inheritedStyle.textDecorations || []),
    ...parseTextDecoration(style.textDecoration),
  ];

  return {
    ...inheritedStyle,
    backgroundColor: style.backgroundColor || inheritedStyle.backgroundColor || "",
    border: BORDER_SIDES.reduce(
      (border, side) => ({
        ...border,
        [side]: getBorderSide(style, side, inheritedStyle),
      }),
      {}
    ),
    color: style.color || inheritedStyle.color || "",
    fontFamily: style.fontFamily || inheritedStyle.fontFamily || "",
    fontSize,
    fontStyle:
      style.fontStyle ||
      (["EM", "I"].includes(tagName) ? "italic" : inheritedStyle.fontStyle || ""),
    fontWeight:
      style.fontWeight ||
      (["B", "STRONG"].includes(tagName) ? "bold" : inheritedStyle.fontWeight || ""),
    indent:
      (inheritedStyle.indent || 0) +
      parsePx(style.marginLeft, 0) +
      (tagName === "BLOCKQUOTE" ? 30 : 0),
    lineHeight: parsePx(style.lineHeight, inheritedStyle.lineHeight || null),
    strikeThrough:
      inheritedStyle.strikeThrough ||
      ["S", "STRIKE"].includes(tagName) ||
      textDecorations.includes("line-through"),
    textAlign: style.textAlign || inheritedStyle.textAlign || "left",
    textDecorations,
    underline:
      inheritedStyle.underline ||
      tagName === "U" ||
      textDecorations.includes("underline"),
    columnCount: parseColumnCount(
      style.columnCount,
      inheritedStyle.columnCount || 1
    ),
  };
};

const collectStyleFromChildren = (element, inheritedStyle = {}) => {
  const ownStyle = getInlineStyle(element, inheritedStyle);

  return Array.from(element.childNodes || []).reduce((style, node) => {
    if (node.nodeType !== 1) return style;

    const childStyle = collectStyleFromChildren(node, ownStyle);

    return {
      ...style,
      fontFamily: childStyle.fontFamily || style.fontFamily,
      fontSize: Math.max(style.fontSize || 30, childStyle.fontSize || 30),
      fontStyle: childStyle.fontStyle || style.fontStyle,
      fontWeight: childStyle.fontWeight || style.fontWeight,
      lineHeight: childStyle.lineHeight || style.lineHeight,
      border: BORDER_SIDES.reduce(
        (border, side) => ({
          ...border,
          [side]:
            (childStyle.border?.[side]?.width || 0) >
            (style.border?.[side]?.width || 0)
              ? childStyle.border[side]
              : style.border?.[side],
        }),
        style.border || {}
      ),
      strikeThrough: style.strikeThrough || childStyle.strikeThrough,
      underline: style.underline || childStyle.underline,
      columnCount: Math.max(style.columnCount || 1, childStyle.columnCount || 1),
    };
  }, ownStyle);
};

const isBlockElement = (element) => BLOCK_TAGS.includes(element?.tagName);

const getBlockChildren = (element) =>
  Array.from(element?.children || []).filter(isBlockElement);

const extractLinesFromElement = (element, inheritedStyle = {}) => {
  const ownStyle = getInlineStyle(element, inheritedStyle);
  const blockChildren = getBlockChildren(element);

  if (isBlockElement(element) && !blockChildren.length) {
    const text = decodeHtml(element.textContent || "").trim();

    return text
      ? [{ text, style: collectStyleFromChildren(element, inheritedStyle) }]
      : [];
  }

  const childLines = Array.from(element.children || []).flatMap((child) =>
    extractLinesFromElement(child, ownStyle)
  );

  if (childLines.length) return childLines;

  const text = decodeHtml(element.textContent || "").trim();

  return text ? [{ text, style: ownStyle }] : [];
};

const extractStyledLines = (html = "") => {
  if (typeof document === "undefined") {
    return stripHtmlToLines(html).map((text) => ({ text, style: {} }));
  }

  const wrapper = document.createElement("div");
  wrapper.innerHTML = sanitizeTemplate(html);

  const lines = extractLinesFromElement(wrapper);

  return lines.length
    ? lines
    : stripHtmlToLines(html).map((text) => ({ text, style: {} }));
};

const isBlackColor = (value = "") =>
  !value ||
  /^#?000(?:000)?$/i.test(String(value).trim()) ||
  /^rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)$/i.test(String(value).trim());

const isTransparentOrWhite = (value = "") =>
  !value ||
  /transparent/i.test(value) ||
  /^#?fff(?:fff)?$/i.test(String(value).trim()) ||
  /^rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)$/i.test(String(value).trim());

export const getUnsupportedZplStyleWarnings = (html = "") => {
  if (typeof document === "undefined") return [];

  const wrapper = document.createElement("div");
  wrapper.innerHTML = sanitizeTemplate(html);
  const warnings = new Set();

  if (wrapper.querySelector("img"))
    warnings.add("Imagens inseridas no layout ainda não são convertidas para ZPL.");

  wrapper.querySelectorAll("*").forEach((element) => {
    const style = element.style || {};

    if (!isBlackColor(style.color))
      warnings.add("Cores de fonte são ignoradas na impressão ZPL monocromática.");
    if (!isTransparentOrWhite(style.backgroundColor))
      warnings.add("Cores de fundo são ignoradas na impressão ZPL monocromática.");
    if (style.fontFamily)
      warnings.add("Família da fonte é aproximada para fontes internas da impressora.");
    if (style.fontStyle === "italic" || ["EM", "I"].includes(element.tagName))
      warnings.add("Itálico não possui equivalente fiel no ZPL atual e será impresso sem inclinação.");
  });

  return Array.from(warnings);
};

const getTextWidth = (text, fontSize) =>
  Math.max(Math.round(String(text).length * fontSize * 0.6), fontSize);

const getAlignedX = ({
  text,
  fontSize,
  printWidth,
  margin,
  textAlign,
  width = null,
}) => {
  const textWidth = getTextWidth(text, fontSize);
  const areaWidth = Math.max(Number(width) || printWidth - margin * 2, fontSize);

  if (textAlign === "center")
    return Math.max(Math.round(margin + (areaWidth - textWidth) / 2), margin);

  if (textAlign === "right")
    return Math.max(margin + areaWidth - textWidth, margin);

  return margin;
};

const buildLine = ({ x, y, width, height, thickness, style = "solid" }) => {
  const lineThickness = Math.round(thickness || 0);

  if (!lineThickness || style === "none") return "";
  if (style === "double") {
    const offset = lineThickness * 2;
    const isHorizontal = width >= height;

    return (
      buildLine({ x, y, width, height, thickness: lineThickness, style: "solid" }) +
      buildLine({
        x: isHorizontal ? x : x + offset,
        y: isHorizontal ? y + offset : y,
        width,
        height,
        thickness: lineThickness,
        style: "solid",
      })
    );
  }

  if (style === "dashed" || style === "dotted") {
    const isHorizontal = width >= height;
    const totalLength = isHorizontal ? width : height;
    const segmentLength = style === "dotted" ? lineThickness : lineThickness * 4;
    const gap = style === "dotted" ? lineThickness * 2 : lineThickness * 2;
    let zpl = "";

    for (let offset = 0; offset < totalLength; offset += segmentLength + gap) {
      const currentLength = Math.min(segmentLength, totalLength - offset);

      zpl += `^FO${isHorizontal ? x + offset : x},${isHorizontal ? y : y + offset}^GB${
        isHorizontal ? currentLength : lineThickness
      },${isHorizontal ? lineThickness : currentLength},${lineThickness}^FS\n`;
    }

    return zpl;
  }

  return `^FO${x},${y}^GB${width},${height},${lineThickness}^FS\n`;
};

const buildBorder = ({ x, y, width, height, border = {} }) => {
  const top = border.top || {};
  const right = border.right || {};
  const bottom = border.bottom || {};
  const left = border.left || {};

  return [
    buildLine({
      x,
      y,
      width,
      height: Math.max(Math.round(top.width || 0), 1),
      thickness: top.width,
      style: top.style,
    }),
    buildLine({
      x: x + width - Math.max(Math.round(right.width || 0), 1),
      y,
      width: Math.max(Math.round(right.width || 0), 1),
      height,
      thickness: right.width,
      style: right.style,
    }),
    buildLine({
      x,
      y: y + height - Math.max(Math.round(bottom.width || 0), 1),
      width,
      height: Math.max(Math.round(bottom.width || 0), 1),
      thickness: bottom.width,
      style: bottom.style,
    }),
    buildLine({
      x,
      y,
      width: Math.max(Math.round(left.width || 0), 1),
      height,
      thickness: left.width,
      style: left.style,
    }),
  ].join("");
};

const getZplFontCode = (fontFamily = "") => {
  if (/courier/i.test(fontFamily)) return "A";
  if (/times/i.test(fontFamily)) return "D";

  return "0";
};

const buildField = ({ x, y, fontOrientation, line, fontSize, style = {} }) => {
  const fontCode = getZplFontCode(style.fontFamily);
  const field = `^FO${x},${y}^A${fontCode}${fontOrientation},${fontSize},${fontSize}^FD${escapeZplField(
    line
  )}^FS`;
  const boldField =
    /bold|[6-9]00/.test(String(style.fontWeight || "")) && fontCode === "0"
      ? `\n^FO${x + 1},${y}^A${fontCode}${fontOrientation},${fontSize},${fontSize}^FD${escapeZplField(
          line
        )}^FS`
      : "";

  return `${field}${boldField}`;
};

const buildTextDecorations = ({
  x,
  y,
  fontSize,
  textWidth,
  textHeight,
  isLandscape,
  style = {},
}) => {
  const thickness = Math.max(Math.round(fontSize / 12), 1);
  let zpl = "";

  if (isLandscape) {
    if (style.underline)
      zpl += buildLine({
        x: x + Math.round(fontSize * 0.15),
        y,
        width: thickness,
        height: textHeight,
        thickness,
      });
    if (style.strikeThrough)
      zpl += buildLine({
        x: x - Math.round(fontSize * 0.45),
        y,
        width: thickness,
        height: textHeight,
        thickness,
      });

    return zpl;
  }

  if (style.underline)
    zpl += buildLine({
      x,
      y: y + fontSize + thickness,
      width: textWidth,
      height: thickness,
      thickness,
    });
  if (style.strikeThrough)
    zpl += buildLine({
      x,
      y: y + Math.round(fontSize / 2),
      width: textWidth,
      height: thickness,
      thickness,
    });

  return zpl;
};

export const buildZplFromHtml = (
  content = "",
  copies = 1,
  orientation = LABEL_ORIENTATIONS.portrait,
  paperWidth = 102
) => {
  const lines = extractStyledLines(content);
  const normalizedOrientation = normalizeOrientation(orientation);
  const isLandscape =
    normalizedOrientation === LABEL_ORIENTATIONS.landscape;
  const paperWidthMm = Math.max(Number(paperWidth) || 102, 1);
  const printWidth = Math.max(
    Math.round(paperWidthMm * ZT231_DOTS_PER_MM),
    1
  );
  const labelLength = 600;
  const fontOrientation = isLandscape ? "R" : "N";
  const margin = 30;
  const columnCount = Math.min(
    Math.max(
      ...lines.map((lineData) => Number(lineData?.style?.columnCount) || 1),
      1
    ),
    4
  );
  const columnGap = columnCount > 1 ? 20 : 0;
  const columnWidth = Math.max(
    Math.floor((printWidth - margin * 2 - columnGap * (columnCount - 1)) / columnCount),
    1
  );
  const rowsPerColumn = Math.max(Math.ceil((lines.length || 1) / columnCount), 1);
  const body = (lines.length ? lines : [" "])
    .map((lineData, index) => {
      const line = lineData.text || " ";
      const style = lineData.style || {};
      const columnIndex = columnCount > 1 ? Math.floor(index / rowsPerColumn) : 0;
      const rowIndex = columnCount > 1 ? index % rowsPerColumn : index;
      const fontSize = Math.min(Math.max(Math.round(style.fontSize || 30), 4), 72);
      const lineHeightValue = Number(style.lineHeight);
      const lineSpacing = Math.max(
        Math.round(
          lineHeightValue && lineHeightValue <= 4
            ? fontSize * lineHeightValue
            : lineHeightValue || fontSize * 1.25
        ),
        fontSize
      );
      const border = style.border || {};

      if (isLandscape) {
        const columnOffset = columnIndex * (columnWidth + columnGap);
        const x = Math.max(printWidth - margin - rowIndex * lineSpacing, margin);
        const y = margin + columnOffset + (style.indent || 0);
        const textHeight = getTextWidth(line, fontSize);
        const textBorder = buildBorder({
          x: x - fontSize - 4,
          y: y - 4,
          width: fontSize + 8,
          height: textHeight + 8,
          border,
        });

        return `${textBorder}${buildTextDecorations({
          x,
          y,
          fontSize,
          textWidth: fontSize,
          textHeight,
          isLandscape,
          style,
        })}${buildField({ x, y, fontOrientation, line, fontSize, style })}`;
      }

      const columnX = margin + columnIndex * (columnWidth + columnGap) + (style.indent || 0);
      const y = margin + rowIndex * lineSpacing;
      const x = getAlignedX({
        text: line,
        fontSize,
        printWidth,
        margin: columnX,
        textAlign: style.textAlign,
        width: columnWidth,
      });
      const textWidth = getTextWidth(line, fontSize);
      const textBorder = buildBorder({
        x: x - 4,
        y: y - 4,
        width: textWidth + 8,
        height: fontSize + 10,
        border,
      });

      return `${textBorder}${buildTextDecorations({
        x,
        y,
        fontSize,
        textWidth,
        textHeight: fontSize,
        isLandscape,
        style,
      })}${buildField({
        x,
        y,
        fontOrientation,
        line,
        fontSize,
        style,
      })}`;
    })
    .join("\n");

  return `^XA
^CI28
^PW${printWidth}
^LL${labelLength}
^PQ${Math.max(Number(copies) || 1, 1)}
${body}
^XZ`;
};

const buildPrinterUrl = ({ ip, port }) => {
  const host = String(ip || "").trim();

  if (!host) throw new Error("A impressora selecionada não possui IP cadastrado");
  if (/^https?:\/\//i.test(host)) {
    const url = new URL(host);
    url.port = String(port);
    return url.toString();
  }

  return `http://${host}:${port}`;
};

export const sendLabelToLocalPrinter = async ({
  printer,
  port = 9100,
  content,
  copies = 1,
  orientation = LABEL_ORIENTATIONS.portrait,
  paperWidth = 102,
}) => {
  const url = buildPrinterUrl({ ip: printer?.ip, port: Number(port) || 9100 });
  const zpl = buildZplFromHtml(content, copies, orientation, paperWidth);
  const warnings = getUnsupportedZplStyleWarnings(content);

  await fetch(url, {
    method: "POST",
    mode: "no-cors",
    cache: "no-store",
    body: zpl,
    headers: {
      "Content-Type": "text/plain",
    },
  });

  return {
    message: warnings.length
      ? "Etiqueta enviada com avisos de impressão"
      : "Etiqueta enviada para a impressora local",
    payload: zpl,
    warnings,
  };
};
